// pages/students/View.tsx
import DetailView from "@/components/DetailView";
import { Spinner, ErrorMessage, CustomAlertDialog } from "@/components";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import InputField from "@/components/form/components/InputField";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchStudentDetailById,
  type BatchStudentDetailType,
  type StudentUpdateType,
} from "@/services/student.service";
import {
  useUpdateStudentDetailMutation,
  useDeleteStudentDetailMutation,
} from "@/hooks/api/student.queries";
import { useAlertDialog } from "@/hooks/useDialogState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Pencil, Trash } from "lucide-react";
import * as z from "zod";

// ─── Edit Dialog ──────────────────────────────────────────────────────────────

const editSchema = z.object({
  name: z.string().optional(),
  phone: z.string().optional(),
  cgpa: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().min(0).max(10).optional()
  ),
  current_semester: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().int().min(1).optional()
  ),
  current_year: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().int().min(1).optional()
  ),
});

type EditFormType = {
  name?: string;
  phone?: string;
  cgpa?: number;
  current_semester?: number;
  current_year?: number;
};

const StudentEditDialog = ({
  isOpen,
  onClose,
  student,
  studentId,
}: {
  isOpen: boolean;
  onClose: () => void;
  student: BatchStudentDetailType;
  studentId: string;
}) => {
  const updateMutation = useUpdateStudentDetailMutation(studentId);

  const form = useForm<EditFormType>({
    resolver: zodResolver(editSchema) as any,
    defaultValues: {
      name: "",
      phone: "",
      cgpa: undefined,
      current_semester: undefined,
      current_year: undefined,
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: "",
        phone: "",
        cgpa: undefined,
        current_semester: student.current_enrollment?.start_year
          ? undefined
          : undefined,
        current_year: undefined,
      });
    }
  }, [isOpen, student, form]);

  const handleSubmit = (values: EditFormType) => {
    // Only send fields that are actually filled in
    const payload: StudentUpdateType = {};
    if (values.name?.trim()) payload.name = values.name.trim();
    if (values.phone?.trim()) payload.phone = values.phone.trim();
    if (values.cgpa != null) payload.cgpa = values.cgpa;
    if (values.current_semester != null) payload.current_semester = values.current_semester;
    if (values.current_year != null) payload.current_year = values.current_year;

    updateMutation.mutate(payload, { onSuccess: onClose });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Edit Student</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">
          Roll number:{" "}
          <span className="font-medium text-gray-800">
            {student.current_enrollment?.roll_number ?? student.roll_number ?? "—"}
          </span>
        </p>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <InputField<EditFormType>
              name="name"
              label="Name"
              placeholder="Full name"
              type="text"
              disabled={updateMutation.isPending}
            />
            <InputField<EditFormType>
              name="phone"
              label="Phone"
              placeholder="Phone number"
              type="tel"
              disabled={updateMutation.isPending}
            />
            <InputField<EditFormType>
              name="cgpa"
              label="CGPA"
              placeholder="e.g. 8.5"
              type="number"
              disabled={updateMutation.isPending}
            />
            <InputField<EditFormType>
              name="current_semester"
              label="Current Semester"
              placeholder="e.g. 3"
              type="number"
              disabled={updateMutation.isPending}
            />
            <InputField<EditFormType>
              name="current_year"
              label="Current Year"
              placeholder="e.g. 2"
              type="number"
              disabled={updateMutation.isPending}
            />
            <DialogFooter className="col-span-full">
              <Button type="button" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Profile Card ─────────────────────────────────────────────────────────────

const StudentProfileCard = ({ student }: { student: BatchStudentDetailType }) => {
  const rollNo =
    student.current_enrollment?.roll_number ?? student.roll_number ?? "—";
  const initials = rollNo.slice(0, 2).toUpperCase();

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="pb-8 pt-8">
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-xl font-bold text-white">
            {initials}
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">{rollNo}</h2>
              {student.current_enrollment?.status && (
                <Badge
                  variant={
                    student.current_enrollment.status === "active"
                      ? "secondary"
                      : "destructive"
                  }
                  className="rounded-full px-3 font-medium capitalize"
                >
                  {student.current_enrollment.status}
                </Badge>
              )}
              {student.current_enrollment?.is_alumni && (
                <Badge variant="outline" className="rounded-full px-3 font-medium">
                  Alumni
                </Badge>
              )}
              {student.is_open_to_work && (
                <Badge
                  variant="outline"
                  className="rounded-full px-3 font-medium text-green-600"
                >
                  Open to Work
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 gap-y-1 text-sm text-gray-500 sm:grid-cols-2">
              {student.current_enrollment?.degree_name && (
                <span>🎓 {student.current_enrollment.degree_name}</span>
              )}
              {student.current_enrollment?.degree_type && (
                <span>📄 {student.current_enrollment.degree_type.toUpperCase()}</span>
              )}
              {student.current_enrollment?.college_name && (
                <span>🏫 {student.current_enrollment.college_name}</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Enrollment Card ──────────────────────────────────────────────────────────

const EnrollmentCard = ({
  title,
  enrollment,
}: {
  title: string;
  enrollment: BatchStudentDetailType["current_enrollment"];
}) => {
  if (!enrollment) return null;

  const rows = [
    { label: "Roll Number", value: enrollment.roll_number },
    { label: "Degree", value: enrollment.degree_name },
    { label: "Degree Type", value: enrollment.degree_type?.toUpperCase() },
    { label: "College", value: enrollment.college_name },
    { label: "Start Year", value: enrollment.start_year },
    { label: "End Year", value: enrollment.end_year ?? "Ongoing" },
    { label: "Status", value: enrollment.status },
    {
      label: "Enrolled",
      value: enrollment.created_at
        ? new Date(enrollment.created_at).toLocaleDateString()
        : null,
    },
  ].filter((r) => r.value != null && r.value !== "");

  return (
    <Card className="border-gray-100 shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-12 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <dt className="text-xs font-medium text-gray-400">{label}</dt>
              <dd className="capitalize text-gray-700">{String(value)}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const StudentsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const alertDialog = useAlertDialog();
  const deleteMutation = useDeleteStudentDetailMutation();

  const {
    data: student,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["students", "detail", id],
    queryFn: () => fetchStudentDetailById(id!),
    enabled: !!id,
  });

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        alertDialog.close();
        navigate(-1);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isError || !student) {
    return (
      <ErrorMessage
        message="Failed to load student details."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <DetailView
        title="Students"
        backPath={-1}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              <Pencil size={14} className="mr-1" />
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() =>
                alertDialog.open({
                  message: `Are you sure you want to delete this student (${student.current_enrollment?.roll_number ?? student.roll_number ?? "Unknown"})?`,
                  mantraId: id ?? "",
                  mantraTitle: "",
                  action: "delete",
                })
              }
            >
              <Trash size={14} className="mr-1" />
              Delete
            </Button>
          </div>
        }
      >
        <StudentProfileCard student={student} />
        <EnrollmentCard
          title="Current Enrollment"
          enrollment={student.current_enrollment}
        />
        {student.last_degree && (
          <EnrollmentCard
            title="Previous Degree"
            enrollment={student.last_degree}
          />
        )}
      </DetailView>

      {/* Edit Dialog */}
      <StudentEditDialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        student={student}
        studentId={id!}
      />

      {/* Delete Confirm */}
      <CustomAlertDialog
        isOpen={alertDialog.state.isOpen}
        onOpenChange={(open) => !open && alertDialog.close()}
        title="Delete Student"
        description={alertDialog.state.message as string}
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        onConfirm={handleDeleteConfirm}
        onCancel={alertDialog.close}
      />
    </>
  );
};

export default StudentsView;
